import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import Search from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useTheme,
	GlobalStyles,
	Paper,
	Divider,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import InputAdornment from '@mui/material/InputAdornment';

import SmallFab from './SmallFab';
import { PromptDataModel, useDeletePromptMutation, useFetchPromptsQuery, useSavePromptMutation } from '../../../../services/promptApi';
import { SaveAsOutlined } from '@mui/icons-material';

const SearchPrompt: React.FC<any> = ({ getSelectedPrompt }) => {
	const [open, setOpen] = useState(false);
	const [filterText, setFilterText] = useState('');
	const [editingPrompt, setEditingPrompt] = useState<PromptDataModel | null>(null);
	const theme = useTheme();

	const { data: prompts, error, isLoading } = useFetchPromptsQuery();
	const { formatMessage } = useIntl();
	const [deletePrompt, { isLoading: isDeleting }] = useDeletePromptMutation();
	const [savePrompt, { isLoading: isSaving }] = useSavePromptMutation();

	if (error) {
		return (
			<SmallFab
				tooltip={formatMessage({ id: 'ai-prompt-bank.error' })}
				loading={isLoading}
				content={<PsychologyAltIcon />}
				onClick={() => toast.error(formatMessage({ id: 'ai-prompt-bank.error' }))}
			/>
		);
	}

	const filteredItems = prompts?.filter(prompt => {
		const isMatch =
			filterText === '' ||
			(prompt.promptTitle ? prompt.promptTitle.toLowerCase().includes(filterText.toLowerCase()) : false) ||
			(prompt.promptCategory ? prompt.promptCategory.toLowerCase().includes(filterText.toLowerCase()) : false) ||
			(prompt.promptContent ? prompt.promptContent.toLowerCase().includes(filterText.toLowerCase()) : false);

		return isMatch;
	});

	const deleteClick = async (promptItem: PromptDataModel) => {
		const deleted = await deletePrompt(promptItem);

		if (deleted.error) {
			toast.error(formatMessage({ id: 'ai-prompt-bank.delete-error' }));
			return;
		}

		toast.success(formatMessage({ id: 'ai-prompt-bank.deleted' }));
	};

	const handleEdit = (prompt: PromptDataModel) => {
		setEditingPrompt(prompt);
	};

	const handleSaveEdit = async () => {
		if (!editingPrompt) return;

		try {
			await savePrompt(editingPrompt).unwrap();
			toast.success(formatMessage({ id: 'ai-prompt-bank.updated' }));
			setEditingPrompt(null);
		} catch (error) {
			toast.error(formatMessage({ id: 'ai-prompt-bank.update-error' }));
		}
	};

	const handleCancelEdit = () => {
		setEditingPrompt(null);
	};

	return (
		<>
			<GlobalStyles
				styles={{
					'.MuiTooltip-popper': {
						zIndex: '40001 !important',
					},
				}}
			/>
			<SmallFab
				tooltip={formatMessage({ id: 'ai-prompt-bank.title' })}
				loading={isLoading || isDeleting}
				content={<WarehouseIcon />}
				onClick={() => setOpen(!open)}
			/>
			<Dialog sx={{ zIndex: 40000 }} open={open} onClose={() => setOpen(!open)} maxWidth='lg' fullWidth>
				<DialogTitle>
					<Stack direction={'row'} justifyContent='space-between'>
						<Box>
							<Typography variant='h6'>{formatMessage({ id: 'ai-prompt-bank.title' })}</Typography>
							<Typography variant='body2' sx={{ fontSize: '.8rem' }}>
								{formatMessage({ id: 'ai-prompt-bank.description' })}
							</Typography>
						</Box>
						<SmallFab aria-label='close' onClick={() => setOpen(false)} content={<CloseIcon />} color={grey[100]} />
					</Stack>
				</DialogTitle>
				<DialogContent>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell colSpan={3}>
									<FormControl fullWidth sx={{ p: 0 }}>
										<TextField
											slotProps={{
												input: {
													endAdornment: (
														<InputAdornment position='start'>
															<Search />
														</InputAdornment>
													),
												},
											}}
											variant='standard'
											placeholder='Vapaasanahaku...'
											value={filterText}
											onChange={e => setFilterText(e.target.value)}
											sx={{ mb: '1rem' }}
										/>
									</FormControl>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredItems?.map((item: any) => (
								<TableRow key={item.promptId}>
									{editingPrompt?.promptId === item.promptId ? (
										<TableCell colSpan={4}>
											<Divider sx={{ mb: 2 }} />
											<Stack spacing={2}>
												<Stack direction='row' spacing={2}>
													<TextField
														variant='standard'
														label='Otsikko'
														fullWidth
														value={editingPrompt?.promptTitle || ''}
														onChange={e =>
															editingPrompt &&
															setEditingPrompt({ ...editingPrompt, promptTitle: e.target.value })
														}
													/>
													<TextField
														variant='standard'
														label='Kategoria'
														fullWidth
														value={editingPrompt?.promptCategory || ''}
														onChange={e =>
															editingPrompt &&
															setEditingPrompt({ ...editingPrompt, promptCategory: e.target.value })
														}
													/>
												</Stack>
												<TextField
													variant='standard'
													label='Sisältö'
													fullWidth
													multiline
													rows={4}
													value={editingPrompt?.promptContent || ''}
													onChange={e =>
														editingPrompt &&
														setEditingPrompt({ ...editingPrompt, promptContent: e.target.value })
													}
												/>
												<Stack direction='row' spacing={1} justifyContent='flex-end'>
													<SmallFab
														tooltip='Tallenna muutokset'
														loading={isSaving}
														onClick={handleSaveEdit}
														content={<SaveAsOutlined />}
														color={theme.palette.success.main}
													/>
													<SmallFab
														tooltip='Peruuta'
														loading={isSaving}
														onClick={handleCancelEdit}
														content={<CloseIcon />}
														color={theme.palette.grey[500]}
													/>
												</Stack>
												<Divider sx={{ mb: 2 }} />
											</Stack>
										</TableCell>
									) : (
										<>
											<TableCell sx={{ verticalAlign: 'top' }}>{item.promptTitle}</TableCell>
											<TableCell sx={{ verticalAlign: 'top' }}>{item.promptCategory}</TableCell>
											<TableCell sx={{ verticalAlign: 'top' }}>
												{item.promptContent && item.promptContent.length > 500
													? `${item.promptContent.substring(0, 500)}...`
													: item.promptContent}
											</TableCell>
											<TableCell sx={{ width: 100 }}>
												<Stack direction='row' justifyContent='space-between'>
													<SmallFab
														tooltip='Kopioi prompt'
														loading={isDeleting}
														onClick={(e: any) => {
															if (editingPrompt !== null) return;
															getSelectedPrompt(item);
															setOpen(false);
														}}
														content={<ContentCopyIcon />}
														color={theme.palette.primary.main}
														disabled={editingPrompt !== null}
													/>
													<SmallFab
														tooltip='Muokkaa prompt'
														loading={isDeleting}
														onClick={() => {
															handleEdit(item);
														}}
														content={<EditIcon />}
														color={theme.palette.info.main}
														disabled={editingPrompt !== null}
													/>
													<SmallFab
														tooltip='Poista prompt'
														loading={isDeleting}
														onClick={(e: any) => {
															if (editingPrompt !== null) return;
															deleteClick(item);
														}}
														content={<DeleteIcon />}
														color={theme.palette.error.main}
														disabled={editingPrompt !== null}
													/>
												</Stack>
											</TableCell>
										</>
									)}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SearchPrompt;
